import { Box, Link, Typography } from "@mui/material";

import type { SalesSignal } from "@xop/dashboard-shared";

import { signalKindLabel, SignalKindIcon } from "../components/SignalKindIcon.js";

/**
 * Vertical timeline of every evidence row the agent attached to an
 * org lead. Newest first. Each entry surfaces:
 *
 *   - the kind icon + label
 *   - a clickable source link
 *   - the agent's short neutral summary of the source
 *   - the agent's reasoning for why it matters (italic, mute)
 *
 * Renders a quiet empty hint instead of nothing when there are no
 * signals — it's a real shape (org with no evidence yet), not an error.
 */
export function EvidenceTimeline({
  signals,
}: {
  signals: SalesSignal[];
}): JSX.Element {
  if (signals.length === 0) {
    return (
      <Typography
        sx={{
          borderRadius: 1,
          border: 1,
          borderStyle: "dashed",
          borderColor: "divider",
          p: 2,
          textAlign: "center",
          fontSize: 12,
          color: "text.secondary",
        }}
      >
        No evidence rows on this lead yet — the agent flagged it from a single
        ambient signal and is still assembling the full chain.
      </Typography>
    );
  }
  return (
    <Box component="ol" aria-label="Evidence timeline" sx={{ display: "grid", gap: 1.5, listStyle: "none", m: 0, p: 0 }}>
      {signals.map((s) => (
        <Box
          component="li"
          key={s.id}
          sx={{
            display: "grid",
            gap: 1,
            borderRadius: 1,
            border: 1,
            borderColor: "divider",
            bgcolor: "background.paper",
            p: 1.5,
          }}
        >
          <Box component="header" sx={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", columnGap: 1.5 }}>
            <SignalKindIcon kind={s.kind} showLabel />
            <Link
              href={s.source_url}
              target="_blank"
              rel="noreferrer noopener"
              underline="hover"
              sx={{ fontSize: 14, fontWeight: 500, color: "primary.main" }}
            >
              {s.source_title || s.source_url}
            </Link>
            <Box
              component="time"
              dateTime={s.captured_at}
              sx={{ ml: "auto", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.05em", color: "text.disabled" }}
            >
              {formatDate(s.captured_at)}
            </Box>
          </Box>
          <Typography sx={{ fontSize: 14, lineHeight: 1.6, color: "text.primary" }}>{s.summary}</Typography>
          {s.agent_reasoning && (
            <Typography sx={{ fontSize: 12, fontStyle: "italic", lineHeight: 1.6, color: "text.secondary" }}>
              <Box component="span" sx={{ fontStyle: "normal", fontWeight: 600, color: "text.disabled" }}>
                Why it matters · {signalKindLabel(s.kind).toLowerCase()}:
              </Box>{" "}
              {s.agent_reasoning}
            </Typography>
          )}
        </Box>
      ))}
    </Box>
  );
}

function formatDate(iso: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}
